import { useUser } from "@shared/utils/hooks/misc.ts";
import {
  EXTEND_SUBSCRIPTION_DURATIONS,
  LanguageNameSpaces,
  SubscriptionDuration,
  SubscriptionPlanType,
} from "@shared/types";
import { isNumber } from "lodash";
import { getPlanPercentageDiscount } from "@shared/features/settings/subscription/utils.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import { useContext, useMemo } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";
import { IS_WEB_VIEW } from "common/constants.ts";
import { isWhitelabelSubscriptionType } from "@shared/utils/misc.ts";

const useChoosePlan = () => {
  const { plans, isUpgrade, subscriptionParams, isExtend, promoCode } =
    useContext(BuySubscriptionContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { user } = useUser();
  const isFreeUser = user?.isFree();

  const { duration: queryDuration, isAllPlans } = subscriptionParams || {};

  const filteredPlans = useMemo(() => {
    const plansList = plans ? Object.values(plans) : [];
    return plansList?.filter((plan) => {
      if (
        IS_WEB_VIEW &&
        isWhitelabelSubscriptionType(plan.subscription_plan_type)
      ) {
        return false;
      }
      if (isUpgrade) {
        return isNumber(plan.upgradePlan?.upgrade_price);
      } else if (!isAllPlans && user?.isFree()) {
        return plan.subscription_plan_type !== user?.subscription_plan_type;
      }
      return true;
    });
  }, [plans, user]);

  const { isFetching } =
    sharedLib.sharedApi.settings.endpoints.getAllPlanDetails.useQueryState(
      undefined
    );

  const getIsAllowChoosePlan = () => {
    const allowUpgrade =
      !!user?.subscription_plan_type &&
      !user?.isMaxSubscription() &&
      !user?.isExpired();
    const allowPurchase = user?.is_trial || user?.isExpired();
    return (
      allowUpgrade ||
      allowPurchase ||
      ((isExtend || user?.has_auto_renewal) && isAllPlans)
    );
  };

  const getPromoCodeDiscount = () => {
    if (promoCode) {
      let durationToDiscount: Record<SubscriptionDuration, number> = {};
      Object.values(plans || {}).map((plan) => {
        for (let duration of EXTEND_SUBSCRIPTION_DURATIONS) {
          const planDiscount = getPlanPercentageDiscount(plan, duration);
          if (
            planDiscount &&
            planDiscount > (durationToDiscount[duration] || 0)
          ) {
            durationToDiscount[duration] = planDiscount;
          }
        }
      });
      return durationToDiscount;
    }
  };

  const promoCodeDiscount = getPromoCodeDiscount();

  const title =
    isUpgrade || (isFreeUser && !isAllPlans)
      ? t("choose_plan.upgrade_your_plan")
      : t("choose_plan.choose_your_plan");

  return {
    isFetching,
    filteredPlans,
    queryDuration,
    getIsAllowChoosePlan,
    promoCodeDiscount,
    title,
  };
};

export default useChoosePlan;
